import * as d3 from 'd3';
import { PillGroup } from '../../pill/PillGroup';
import { ColorScale } from './ColorScale';

export class Legend {

  /**
   * Common legend component for charts
   * @param {Object} props - The properties to initialize the legend
   * @param {HTMLElement} props.element - The DOM element to append the legend
   * @param {Object} props.data - The data necessary to populate the legend
   * @param {Function} props.draw - The chart draw function to update
   * @param {Object} [props.margin] - The top, right, bottom and left margins of the chart
   * @param {number} [props.margin.top] - The top margin
   * @param {number} [props.margin.right] - The right margin
   * @param {number} [props.margin.bottom] - The bottom margin
   * @param {number} [props.margin.left] - The left margin
   */
  constructor(props) {
    this.element = props.element;
    this.data = props.data;
    this.draw = props.draw;
    this.pillsGroup = props.pillsGroup || 'series-group';
    this.highlight = props.highlight || (() => {});
    this.margin = props.margin;
    this.identifierScale = props.identifierScale || null;
    this.columns = this.data.length > 0 ? Object.keys(this.data[0]) : this.data;
    this.colorScale = props.colorScale || this._newColorScale();
    this.allPillsSelected = true;
    this.pills = [];
  }

  _newColorScale() {
    const colorScale = new ColorScale({
      data: this.data
    });
    colorScale.init();
    return colorScale;
  }

  /**
   * Initialize the legend
   */
  init() {
    this.legend = this._createLegend();
    this._addPillsGroup(this.pillsGroup, this.data.series);
  }

  _createLegend() {
    return this._selectOrAppend(
      d3.select(this.element),
      'chart-legend'
    )
      .lower();
  }

  _selectOrAppend(element, className, type = 'div', attributes = {}) {
    if(element.select(`.${className}`).empty()) {
      return this._attrs(
        element.append(type)
          .classed(className, true),
        attributes
      );
    }
    return element.select(`.${className}`);
  }

  _attrs(element, attributes) {
    for (const key in attributes) {
      if (Object.prototype.hasOwnProperty.call(attributes, key)) {
        element.attr(key, attributes[key]);
      }
    }
    return element;
  }

  _addPillsGroup(className, data) {
    const group = this._selectOrAppend(
      this.legend,
      className
    );
    const pills = this._addData(group, data);
    this._appendContents(pills);
  }

  _addData(element, data) {
    const pills = element
      .selectAll('.pill')
      .data(data)
      .enter()
      .append('span')
      .attr('data-key', d => d.name)
      .classed('pill', true)
      .classed('toggle', true)
      .on('mouseenter', (d) => {
        const activePill = this.pills.filter(p => p.key === d.name)[0];
        if (activePill.instance.isSelected()) {
          this.highlight(d.name);
        } else {
          this.highlight();
        }
      })
      .on('mouseleave', () => {
        this.highlight();
      });

    this.selectAll = element
      .selectAll('.toggle-all')
      .data(['Select all'])
      .enter()
      .append('a')
      .attr('href', 'javascript:void(0)')
      .classed('toggle-all', true)
      .text('Select all');

    this._initPills.bind(this)(element);

    return pills;
  }

  _appendContents(pill) {
    if(this.identifierScale) {
      this._addIdentifier(pill);
    } else {
      this._appendIcon(pill);
    }
    pill
      .append('span')
      .text(d => d.name);
  }

  _addIdentifier(pill) {
    const svg = this._appendSVG(pill);
    const identifiers = this._bindIdentifiers(svg);
    const enter = identifiers.enter();
    this._appendSeparator(enter);
    this._appendIdentifier(enter);
  }

  _appendSVG(selection) {
    return selection
      .append('svg')
      .datum(d => this._getIdentifierData(d))
      .attr('width', d => d.length * 16)
      .attr('height', '16')
      .attr('viewBox', d => `-4 -4 ${d.length * 8} 8`);
  }

  _bindIdentifiers(selection) {
    return selection
      .selectAll('path')
      .data(d => d);
  }

  _appendSeparator(selection){
    return selection
      .filter((_,i) => i > 0)
      .append('path')
      .classed('id-separator', true)
      .attr('d', (_, i) => {
        const left =  (i * 8) + 4;
        return `M${left},-3L${left},3`;
      });
  }

  _appendIdentifier(selection) {
    return selection
      .append('path')
      .attr('d', d => `${d.draw()}`)
      .attr('class', d => `identifier ${d.color} ${d.name}`)
      .attr('transform', (_, i) => `translate(${i * 8} 0)`);
  }

  _appendIcon(selection) {
    selection
      .append('i')
      .attr('class', d => `icon icon-alarm-level6 ${this.colorScale.get()(d.name)}`);
  }

  /**
   * Retrieve the correct identifier function
   * @param {Object} data - The data of an individual marker
   * @return {function} - The correct identifier function
   * @private
   */
  _getIdentifierData(data){
    const identifier = this.identifierScale.get()(data.name);
    const color = this.colorScale.get()(data.name);
    return this._getIdentifiers(data).map(override => {
      if(override){
        return {
          name: override,
          draw: this.identifierScale[override].bind(this.identifierScale),
          color
        };
      }
      return {
        ...identifier,
        color
      };
    });
  }

  _getIdentifiers(data) {
    return [data.identifier].concat(
      data.markers
        .map(m => m.identifier)
        .filter(id => id !== undefined)
    );
  }

  _initPills (pillsGroup) {
    const pillGroup = new PillGroup({
      element: pillsGroup.node(),
      action: () => {
        let dataCopy = JSON.parse(JSON.stringify(this.data));
        let allPillsUnselected = true;
        this.pills.forEach(pill => {
          if (!pill.instance.isSelected()) {
            dataCopy.series = dataCopy.series.filter(serie => serie.name !== pill.key);
          } else {
            allPillsUnselected = false;
          }
        });
        if (allPillsUnselected) dataCopy = { common: [], series: []};
        this.draw(dataCopy);
        this.highlight();
      }
    });
    pillGroup.init();
    this.pills = pillGroup.getPills().map(pill => ({
      instance: pill,
      key: pill.data.key
    }));
  }

  /**
   * Filter the data according to the pills.
   */
  filter(data) {
    const ret = {};
    const dataCopy = JSON.parse(JSON.stringify(data));

    ret.series = dataCopy.series.filter(serie => {
      let active = false;
      this.pills.forEach(pill => {
        if (pill.key === serie.name && pill.instance.isSelected()) {
          active = true;
        }
      });
      return active;
    });
    if (ret.series.length === 0) {
      ret.common = [];
    } else {
      ret.common = dataCopy.common;
    }
    return ret;
  }
}
